import { resourcePackContents,
  blackMarketOffer, BUILDER_PRODUCT, buyBuilder, buySpeedUpPack, cancelQueued, CrateOffer, finishNow, finishNowPrice, GearItem, MarketOffer, nextBuilderPrice, offersAllowed,
  OfferScreen, purchasesAllowed, QueuedUpgrade, sellToTradingPost, tradingPostStock, UpgradeJob, UpgradeTarget, queueUpgrade, utcDay, Wallet,
} from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";

/**
 * The offline economy's use cases (ADR 0013, FINISH_PLAN WP1): the Shop's gem products, Supply Crates, the Trading
 * Post and the Black Market, Fortress Tribute and the upgrade queue, over the catalog's profile and economy state.
 * Every purchase is refused outside the hub (the never list: no purchase prompt in a level or on the end screen); the
 * caller persists after a success (LevelCatalog.applyTo writes the sections), and nothing is sold while the save cannot
 * be written (`LevelCatalog.canPersist`: another tab has it), as it would be lost. Real-money gem packs are
 * `PurchaseService`'s (purchases.ts).
 */
export class Economy {
  constructor(private readonly catalog: LevelCatalog) {}

  get wallet(): Wallet { return this.catalog.economy.wallet; }
  /** The level offers are sized for: the highest cleared, at least 1. */
  get level(): number { return Math.max(1, this.catalog.highestLevelCleared); }
  private get playerId(): string { return this.catalog.playerId; }

  /** Whether the game may put an offer in front of the player now (meta/shop.ts offersAllowed). */
  offersAllowed(screen: OfferScreen, now: number): boolean {
    return offersAllowed(this.catalog.balance, screen, now, this.catalog.economy.shop.lastLossAt);
  }

  /**
   * @deprecated Builders are real money only since 2026-10-19 (docs/SHOP.md §18): always null. The next builder is a
   * Store offer: `LevelCatalog.deals.nextBuilder(now, ctx)`.
   */
  nextBuilderPrice(): number | null { return nextBuilderPrice(this.catalog.balance, this.catalog.profile.buildersOwned); }

  /**
   * Buys a gem-priced product by id: a speed-up pack or a resource pack (shop.json). `builder` is refused since builders
   * became real money ("builders are sold in the Store"; docs/SHOP.md §18). Crates and market items are bought as shown
   * (buyCrate, buyFromMarket, buyBlackMarket).
   */
  buy(productId: string, now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    const p = this.catalog.profile, eco = this.catalog.economy;
    if (productId === BUILDER_PRODUCT) return buyBuilder(p, eco.wallet, now);
    if (this.catalog.balance.commerce.shop.speedUpPacks.some((x) => x.id === productId)) return buySpeedUpPack(p, eco.wallet, productId, now);
    const res = eco.shop.buyResourcePack(p, eco.wallet, productId, now, this.level);
    return typeof res === "string" ? res : true;
  }

  // ------------------------------------------------------------------ the free daily gold pack (owner 2026-10-21)

  /** Today's free gold pack: its id, gold at the player's level, and whether it is still to claim; null when none. */
  freePack(now: number): { id: string; gold: number; open: boolean } | null {
    const B = this.catalog.balance, pack = B.commerce.shop.resourcePacks.find((p) => p.freePerDay > 0);
    const gold = pack ? resourcePackContents(B, pack.id, this.level) : null;
    return pack && gold !== null ? { id: pack.id, gold, open: this.catalog.economy.shop.freePackOpen(now) } : null;
  }

  /** 1 while today's free gold pack waits (Shop → Supplies): its red dot there and on the hub's Shop tab (SHOP.md §20). */
  freePackDot(now: number): number { return this.freePack(now)?.open ? 1 : 0; }

  claimFreePack(now: number, screen: OfferScreen = "hub"): number | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    return this.catalog.economy.shop.claimFreePack(this.catalog.profile, now, this.level);
  }

  // ------------------------------------------------------------------ Supply Crates (D6; retired 2026-10-21: no slots in shop.json)

  crateOffers(now: number): { offer: CrateOffer; bought: boolean }[] {
    return this.catalog.economy.shop.crateOffers(this.catalog.balance, this.playerId, now, this.level);
  }

  /** Today's free crate (MP-08: first in the row, claimed for nothing once a UTC day; bought through `buyCrate`), or null. */
  freeCrate(now: number): { offer: CrateOffer; bought: boolean } | null {
    return this.catalog.economy.shop.freeCrateOffer(this.catalog.balance, this.playerId, now, this.level);
  }

  buyCrate(shown: CrateOffer, now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    const eco = this.catalog.economy;
    return eco.shop.buyCrate(this.catalog.balance, this.playerId, this.catalog.profile, eco.wallet, shown, now, this.level);
  }

  // ------------------------------------------------------------------ NPC market (D7)

  sellToTradingPost(itemId: number, now: number, confirmMythic = false): { marks: number; refund: number } | string {
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    return sellToTradingPost(this.catalog.profile, this.wallet, itemId, now, confirmMythic);
  }

  /** Today's Trading Post stock with whether each item is already bought. */
  marketStock(now: number): { offer: MarketOffer; bought: boolean }[] {
    const m = this.catalog.economy.market;
    return tradingPostStock(this.catalog.balance, this.playerId, utcDay(now), this.level).map((offer) => ({ offer, bought: m.stockBought(now, offer.index) }));
  }

  /** @deprecated Shop pass 2: the Mystery Merchant (`LevelCatalog.shops`) sells the Trading Post's gear; no UI calls this. */
  buyFromMarket(shown: MarketOffer, now: number, screen: OfferScreen = "hub"): GearItem | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    return this.catalog.economy.market.buyStock(this.catalog.profile, this.wallet, this.playerId, shown, now, this.level);
  }

  blackMarket(now: number): { offer: MarketOffer; bought: boolean } | null {
    const offer = blackMarketOffer(this.catalog.balance, this.playerId, utcDay(now), this.level);
    return offer ? { offer, bought: this.catalog.economy.market.blackMarketBought(now) } : null;
  }

  buyBlackMarket(shown: MarketOffer, now: number, screen: OfferScreen = "hub"): GearItem | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    return this.catalog.economy.market.buyBlackMarket(this.catalog.profile, this.wallet, this.playerId, shown, now, this.level);
  }

  // ------------------------------------------------------------------ Fortress Tribute

  /**
   * What the Tribute holds now (it starts filling at the first look once unlocked), VIP resource production included;
   * `vip` is the part production adds (docs/SHOP.md §8.3: "+n VIP"), zero at VIP 0.
   */
  tribute(now: number): { pay: number; vip: number; full: boolean; unlocked: boolean } {
    const t = this.catalog.economy.tribute;
    t.sync(now, this.catalog.highestLevelCleared);
    return { pay: t.accrued(now, this.level), vip: t.vipBonus(now, this.level), full: t.isFull(now), unlocked: t.unlocked(this.catalog.highestLevelCleared) };
  }

  /** Takes the Tribute's gold (× VIP resource production: Tribute.resourcePercent, set by Deals.syncPerks); null while it holds nothing. */
  claimTribute(now: number): number | null {
    const t = this.catalog.economy.tribute;
    t.sync(now, this.catalog.highestLevelCleared);
    const pay = t.claim(now, this.level);
    if (pay) this.catalog.profile.addGold(pay);
    return pay;
  }

  // ------------------------------------------------------------------ upgrade queue (D4)

  queueUpgrade(target: UpgradeTarget, towerIndex: number, now: number): QueuedUpgrade | string {
    return queueUpgrade(this.catalog.profile, target, towerIndex, now);
  }

  cancelQueued(id: number): number | null { return cancelQueued(this.catalog.profile, id); }

  // ------------------------------------------------------------------ FINISH NOW (MP-07 (a))

  /** Gems to finish a running upgrade now (0 under VIP's free finish), or why it cannot be. */
  finishNowPrice(jobId: number, now: number): number | string { return finishNowPrice(this.catalog.profile, jobId, now); }

  /**
   * Finishes a running upgrade now, for the gems the player was shown (`shownPrice`; free under VIP's free finish), and
   * claims what completed: the jobs, or why not. Hub only, and never while the save cannot be written.
   */
  finishNow(jobId: number, shownPrice: number, now: number, screen: OfferScreen = "hub"): UpgradeJob[] | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    const paid = finishNow(this.catalog.profile, this.wallet, jobId, now, shownPrice);
    return typeof paid === "string" ? paid : this.catalog.profile.claimCompleted(now);
  }
}
